import React from 'react';

import {fieldErrorStyle, fieldHelpStyle, fieldLabelStyle, requiredMarkStyle} from '../lib/formStyles.js';

// ============================================================================
// Field — label, control, help and error (Design Constitution §4 Forms, I10,
// I11, Q35, Q36)
// ----------------------------------------------------------------------------
//   label:    above the control, 13px / 600, Title Case, units in the label
//             ('Feed (lbs)')
//   required: a red '*'; no '(optional)' marks
//   help:     one gray 12.5px line under the control; examples live here, never
//             in a placeholder (Q36)
//   error:    phrased as the fix; replaces the help while shown
// The single child control gets the id, aria-describedby, aria-invalid and
// required wiring, so callers write <Field label="Date"><DateInput … /></Field>.
// ============================================================================

type FieldProps = {
  label: React.ReactNode;
  id?: string;
  required?: boolean;
  help?: React.ReactNode;
  error?: React.ReactNode;
  style?: React.CSSProperties;
  children: React.ReactElement;
};

type WiredProps = {
  id?: string;
  required?: boolean;
  'aria-describedby'?: string;
  'aria-invalid'?: boolean | 'true' | 'false';
  'aria-labelledby'?: string;
};

export default function Field({label, id, required = false, help, error, style, children}: FieldProps) {
  const autoId = React.useId();
  const own = children.props as WiredProps;
  const controlId = id || own.id || `field-${autoId.replace(/:/g, '')}`;
  const labelId = `${controlId}-label`;
  const hasError = error != null && error !== false && error !== '';
  const note = hasError ? error : help;
  const noteId = note != null && note !== false && note !== '' ? `${controlId}-${hasError ? 'error' : 'help'}` : '';
  // Merge with what the child already carries: its own description ids, its
  // own invalid or required state. The label names grouped controls (a
  // segmented row, choice tiles) through aria-labelledby, which <label for>
  // cannot reach.
  const describedBy = [own['aria-describedby'], noteId].filter(Boolean).join(' ');
  const ownInvalid = own['aria-invalid'] === true || own['aria-invalid'] === 'true';
  const effectiveRequired = required || Boolean(own.required);
  const control = React.cloneElement(children as React.ReactElement<WiredProps>, {
    id: controlId,
    required: effectiveRequired || undefined,
    'aria-describedby': describedBy || undefined,
    'aria-invalid': hasError || ownInvalid || undefined,
    'aria-labelledby': own['aria-labelledby'] || labelId,
  });
  return (
    <div data-field="1" style={style}>
      <label id={labelId} htmlFor={controlId} style={fieldLabelStyle}>
        {label}
        {effectiveRequired && (
          <span aria-hidden="true" style={requiredMarkStyle}>
            *
          </span>
        )}
      </label>
      {control}
      {noteId && (
        <div
          id={noteId}
          data-field-note={hasError ? 'error' : 'help'}
          style={hasError ? fieldErrorStyle : fieldHelpStyle}
        >
          {note}
        </div>
      )}
    </div>
  );
}
